import {
    useState, useCallback, forwardRef, useImperativeHandle
} from 'react'
import { useForm } from 'react-hook-form'
import { Form, InputGroup } from 'react-bootstrap'

const numPattern = { value: /^[0-9\b]+$/, message: 'Value must be a number!' }

const ZONE_DEFAULTS = {
    penaltyAreaLength: '1650',
    penaltyAreaWidth: '4032',
    goalAreaLength: '550',
    goalAreaWidth: '1832',
    goalWidth: '732',
}

const FieldSubmitForm = function ({
    width, height, isZoneStage,
    penaltyAreaLength, penaltyAreaWidth, goalAreaLength, goalAreaWidth, goalWidth,
    zoneEnabled,
    onChange, onSubmit
}, ref) {
    const formInputs = {
        width, height,
        penaltyAreaLength: penaltyAreaLength || ZONE_DEFAULTS.penaltyAreaLength,
        penaltyAreaWidth: penaltyAreaWidth || ZONE_DEFAULTS.penaltyAreaWidth,
        goalAreaLength: goalAreaLength || ZONE_DEFAULTS.goalAreaLength,
        goalAreaWidth: goalAreaWidth || ZONE_DEFAULTS.goalAreaWidth,
        goalWidth: goalWidth || ZONE_DEFAULTS.goalWidth,
    }

    const [zones, setZones] = useState({
        penalty: zoneEnabled?.penalty ?? false,
        goalArea: zoneEnabled?.goalArea ?? false,
        goalposts: zoneEnabled?.goalposts ?? false,
    })

    const {
        register, reset, getValues, handleSubmit, formState: { errors }
    } = useForm({ ...formInputs, mode: 'onBlur' })
    const [submitStatus] = useState(false)
    const [validated] = useState(false)

    useImperativeHandle(
        ref,
        () => ({
            onValuesUpdate({ width, height, penaltyAreaLength, penaltyAreaWidth, goalAreaLength, goalAreaWidth, goalWidth, zoneEnabled: ze }) {
                reset({
                    width, height,
                    penaltyAreaLength: penaltyAreaLength || ZONE_DEFAULTS.penaltyAreaLength,
                    penaltyAreaWidth: penaltyAreaWidth || ZONE_DEFAULTS.penaltyAreaWidth,
                    goalAreaLength: goalAreaLength || ZONE_DEFAULTS.goalAreaLength,
                    goalAreaWidth: goalAreaWidth || ZONE_DEFAULTS.goalAreaWidth,
                    goalWidth: goalWidth || ZONE_DEFAULTS.goalWidth,
                })
                if (ze) setZones(ze)
            },
            submit() {
                handleSubmit(onSubmit)()
            },
            getZoneEnabled() {
                return zones
            }
        })
    )

    const onChangeCallback = useCallback(() => {
        const formValues = getValues()
        onChange({ ...formValues, zoneEnabled: zones })
    }, [getValues, onChange, zones])

    const toggleZone = (key) => {
        setZones((prev) => {
            const next = { ...prev, [key]: !prev[key] }
            // Notify parent about change
            const formValues = getValues()
            onChange({ ...formValues, zoneEnabled: next })
            return next
        })
    }

    return (
        <Form
            ref={ref}
            className="field-form w-100"
            validated={validated}
            onChange={onChangeCallback}
        >
            <InputGroup className="mb-1" hasValidation>
                <InputGroup.Text className="w-30">Width (cm.)</InputGroup.Text>
                <Form.Control
                    {...register('width', { pattern: numPattern })}
                    isInvalid={!!errors.width}
                    disabled={submitStatus}
                />
                {errors.width?.message ? (
                    <Form.Control.Feedback type="invalid">{errors.width.message}</Form.Control.Feedback>
                ) : <div className="feedback-mock" />}
            </InputGroup>
            <InputGroup className="mb-1" hasValidation>
                <InputGroup.Text className="w-30">Height (cm.)</InputGroup.Text>
                <Form.Control
                    {...register('height', { pattern: numPattern })}
                    isInvalid={!!errors.height}
                    disabled={submitStatus}
                />
                {errors.height?.message ? (
                    <Form.Control.Feedback type="invalid">{errors.height.message}</Form.Control.Feedback>
                ) : <div className="feedback-mock" />}
            </InputGroup>

            {isZoneStage && (
                <>
                    <hr className="my-2" />
                    <small className="text-muted d-block mb-2">Зоны для коррекции координат</small>

                    <Form.Check
                        type="switch"
                        id="zone-penalty"
                        label="Штрафная площадь"
                        checked={zones.penalty}
                        onChange={() => toggleZone('penalty')}
                        className="mb-1"
                    />
                    {zones.penalty && (
                        <>
                            <InputGroup className="mb-1 ms-4" size="sm">
                                <InputGroup.Text style={{minWidth: 140}}>Длина (см)</InputGroup.Text>
                                <Form.Control {...register('penaltyAreaLength', { pattern: numPattern })} placeholder="1650" />
                            </InputGroup>
                            <InputGroup className="mb-1 ms-4" size="sm">
                                <InputGroup.Text style={{minWidth: 140}}>Ширина (см)</InputGroup.Text>
                                <Form.Control {...register('penaltyAreaWidth', { pattern: numPattern })} placeholder="4032" />
                            </InputGroup>
                        </>
                    )}

                    <Form.Check
                        type="switch"
                        id="zone-goal-area"
                        label="Вратарская площадь"
                        checked={zones.goalArea}
                        onChange={() => toggleZone('goalArea')}
                        className="mb-1"
                    />
                    {zones.goalArea && (
                        <>
                            <InputGroup className="mb-1 ms-4" size="sm">
                                <InputGroup.Text style={{minWidth: 140}}>Длина (см)</InputGroup.Text>
                                <Form.Control {...register('goalAreaLength', { pattern: numPattern })} placeholder="550" />
                            </InputGroup>
                            <InputGroup className="mb-1 ms-4" size="sm">
                                <InputGroup.Text style={{minWidth: 140}}>Ширина (см)</InputGroup.Text>
                                <Form.Control {...register('goalAreaWidth', { pattern: numPattern })} placeholder="1832" />
                            </InputGroup>
                        </>
                    )}

                    <Form.Check
                        type="switch"
                        id="zone-goalposts"
                        label="Ворота (штанги)"
                        checked={zones.goalposts}
                        onChange={() => toggleZone('goalposts')}
                        className="mb-1"
                    />
                    {zones.goalposts && (
                        <InputGroup className="mb-1 ms-4" size="sm">
                            <InputGroup.Text style={{minWidth: 140}}>Ширина (см)</InputGroup.Text>
                            <Form.Control {...register('goalWidth', { pattern: numPattern })} placeholder="732" />
                        </InputGroup>
                    )}
                </>
            )}
        </Form>
    )
}

export default forwardRef(FieldSubmitForm)
